import React, { useEffect, useMemo, useState } from 'react';
import { X, PenLine, AlertTriangle } from 'lucide-react';

// Zincir dışı (borsa) swap'ı ELLE ekle.
//
// Neden gerekti: bir grubun sermayesi zincirde doğmuyor olabilir — ör. Bybit EU'da
// alınan SUI cüzdana transfer olarak geliyor, swap olarak değil. O yüzden grup
// "−264 SUI" gibi eksik bir hikâye gösteriyordu: satış görünüyor, alış görünmüyor.
// Burada yazılan satır zincir üstü bir swap'la AYNI yoldan geçiyor (delta, tur
// eşleme, mutabakat) — tek farkı digest yerine "manual-…" imzası olması.
//
// KOMİSYON İÇİN AYRI ALAN YOK, bilerek: hesaptan düşen BRÜT ile hesaba geçen NET
// yazılır, komisyon böylece kurun içinde kalır. Zincir üstü swap'ta da durum aynı.

interface Props {
  dark?: boolean;
  wallet: string;
  groups: any[];
  groupLabel: (g: any, rank: number) => string;
  sendWs: (m: any) => void;
  subscribe: (fn: (m: any) => void) => (() => void) | undefined;
  onClose: () => void;
  onSaved: () => void;
}

const yerelSimdi = () => {
  const d = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 16);
};

export default function ManualSwapModal({ dark = true, wallet, groups, groupLabel,
                                          sendWs, subscribe, onClose, onSaved }: Props) {
  const [ne, setNe]           = useState(yerelSimdi());
  const [inSym, setInSym]     = useState('USDC');
  const [inAmt, setInAmt]     = useState('');
  const [outSym, setOutSym]   = useState('SUI');
  const [outAmt, setOutAmt]   = useState('');
  const [grup, setGrup]       = useState('0');
  const [rol, setRol]         = useState('delta');
  const [not_, setNot]        = useState('');
  const [busy, setBusy]       = useState(false);
  const [hata, setHata]       = useState('');

  const card  = dark ? 'bg-[#0a1628] border-[#164a9e]' : 'bg-white border-[#d4e0f0]';
  const sub   = dark ? 'bg-[#061530] border-[#0a2560]' : 'bg-[#eef3fa] border-[#d4e0f0]';
  const muted = dark ? 'text-blue-300/70' : 'text-slate-500';
  const inp   = dark ? 'bg-[#03112b] border-[#0a2560] text-blue-50'
                     : 'bg-white border-[#d4e0f0] text-slate-800';

  useEffect(() => {
    const unsub = subscribe((m: any) => {
      if (m.type !== 'manual_swap_saved') return;
      setBusy(false);
      if (m.payload?.success) { onSaved(); onClose(); }
      else setHata(m.payload?.error || 'kaydedilemedi');
    });
    return () => unsub?.();
  }, [subscribe, onSaved, onClose]);

  const ia = parseFloat(inAmt.replace(',', '.')) || 0;
  const oa = parseFloat(outAmt.replace(',', '.')) || 0;
  const iS = inSym.trim().toUpperCase(), oS = outSym.trim().toUpperCase();
  const gecerli = !!wallet && ia > 0 && oa > 0 && !!iS && !!oS && iS !== oS;

  // Kur iki yönde de gösterilir: hangi tarafı kafanda tuttuğun duruma göre değişiyor.
  const kur = useMemo(() => {
    if (!(ia > 0 && oa > 0)) return null;
    const f = (n: number) => n >= 1 ? n.toLocaleString('en-US', { maximumFractionDigits: 6 })
                                    : parseFloat(n.toPrecision(6)).toString();
    return { ileri: `1 ${iS} = ${f(oa / ia)} ${oS}`, geri: `1 ${oS} = ${f(ia / oa)} ${iS}` };
  }, [ia, oa, iS, oS]);

  const kaydet = () => {
    if (!gecerli || busy) return;
    setHata(''); setBusy(true);
    sendWs({ type: 'add_manual_swap', payload: {
      wallet, ts: new Date(ne).getTime(),
      inSymbol: iS, inAmount: ia, outSymbol: oS, outAmount: oa,
      groupNo: parseInt(grup, 10) || 0, role: rol, note: not_,
    } });
  };

  const alan = `w-full px-2 py-1.5 rounded-lg border text-xs ${inp}`;
  const etiket = `text-[10px] ${muted} mb-0.5 block`;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className={`w-[520px] max-w-full rounded-xl border ${card} p-4 shadow-2xl max-h-[88vh] overflow-y-auto`}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-1">
          <PenLine size={15} className="text-sky-400" />
          <span className="font-semibold text-sm">Elle swap ekle — zincir dışı</span>
          <button onClick={onClose} className={`ml-auto ${muted} hover:text-rose-400`}><X size={16} /></button>
        </div>
        <p className={`text-[11px] mb-3 ${muted}`}>
          Borsada yapılan bacaklar için (Bybit, Binance…). Hesabından <b>düşen brüt</b> ile
          hesabına <b>geçen net</b> miktarı yaz — komisyon böylece kurun içinde kalır,
          ayrı alan gerekmez. Cüzdan <span className="font-mono">{wallet.slice(0, 8)}…{wallet.slice(-4)}</span>
        </p>

        <div className="grid grid-cols-2 gap-2.5">
          <div className="col-span-2">
            <label className={etiket}>Tarih / saat</label>
            <input type="datetime-local" value={ne} onChange={e => setNe(e.target.value)} className={alan} />
          </div>

          <div className={`col-span-2 grid grid-cols-2 gap-2.5 rounded-lg border ${sub} p-2.5`}>
            <div>
              <label className={etiket}>VERDİĞİN — miktar</label>
              <input value={inAmt} onChange={e => setInAmt(e.target.value)} placeholder="889.19"
                inputMode="decimal" className={`${alan} tabular-nums text-rose-300`} />
            </div>
            <div>
              <label className={etiket}>token</label>
              <input value={inSym} onChange={e => setInSym(e.target.value)} placeholder="USDC"
                className={`${alan} uppercase`} />
            </div>
            <div>
              <label className={etiket}>ALDIĞIN — miktar</label>
              <input value={outAmt} onChange={e => setOutAmt(e.target.value)} placeholder="990.50"
                inputMode="decimal" className={`${alan} tabular-nums text-emerald-300`} />
            </div>
            <div>
              <label className={etiket}>token</label>
              <input value={outSym} onChange={e => setOutSym(e.target.value)} placeholder="SUI"
                className={`${alan} uppercase`} />
            </div>
            <div className={`col-span-2 text-[11px] tabular-nums ${muted} text-center`}>
              {kur ? <>{kur.ileri} <span className="opacity-50">·</span> {kur.geri}</> : 'kur için iki miktarı da gir'}
            </div>
          </div>

          <div>
            <label className={etiket}>Grup</label>
            <select value={grup} onChange={e => setGrup(e.target.value)} className={alan}>
              <option value="0">— gruba bağlama —</option>
              {groups.filter((g: any) => (!g.wallet || g.wallet === wallet) && g.status !== 'closed')
                .sort((a: any, b: any) => a.rank - b.rank)
                .map((g: any) => <option key={g.no} value={g.no}>{groupLabel(g, g.rank)}</option>)}
            </select>
          </div>
          <div>
            <label className={etiket}>Rol</label>
            <select value={rol} onChange={e => setRol(e.target.value)} className={alan}>
              <option value="delta">Δ delta</option>
              <option value="hedef">$ hedef</option>
              <option value="">rol yok</option>
            </select>
          </div>

          <div className="col-span-2">
            <label className={etiket}>Not (kaynak)</label>
            <input value={not_} onChange={e => setNot(e.target.value)} placeholder="Bybit EU — EUR girişinden"
              className={alan} />
          </div>
        </div>

        {iS && oS && iS === oS && (
          <p className="text-amber-400 text-[11px] mt-2 flex items-start gap-1.5">
            <AlertTriangle size={11} className="mt-0.5 shrink-0" /> Giren ve çıkan token aynı olamaz.
          </p>
        )}
        {hata && <p className="text-rose-400 text-[11px] mt-2">{hata}</p>}

        <div className="flex items-center gap-2 mt-3">
          <button onClick={kaydet} disabled={!gecerli || busy}
            className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold bg-sky-600/25 border border-sky-500/50 text-sky-300 hover:bg-sky-600/40 disabled:opacity-40">
            {busy ? 'Kaydediliyor…' : 'Ekle'}
          </button>
          <button onClick={onClose} className={`px-3 py-2 rounded-lg text-xs border ${sub} ${muted}`}>Kapat</button>
        </div>
      </div>
    </div>
  );
}
